import { Controller } from "@hotwired/stimulus"

// The compose modal is five collapsed rows (Komu · Kópie · Správa · Prílohy ·
// Platba), each summarising a decision the user usually does not need to open.
// Everything here exists to keep a COLLAPSED summary true: the moment a summary
// states something the expanded controls have already contradicted, the row
// stops being a shortcut and becomes a lie the user only catches after sending.
export default class extends Controller {
  static targets = [
    "langPicker",
    "langBtn",
    "source",
    "editToggle",
    "editMode",
    "previewSubject",
    "previewMessage",
    "subjectField",
    "messageField",
    "subjectHidden",
    "messageHidden",
    "localeHidden",
    "copiesSummary",
    "ccField",
    "bccField",
    "attachmentsSummary",
    "attachmentBox"
  ]

  static values = {
    translations: { type: Object, default: {} }
  }

  connect() {
    this.isEditMode = false
    this.currentLang = this.langBtnTargets.find((btn) => btn.dataset.active === "true")?.dataset.lang || "sk"
    this.#reopenEditorForCustomText()
  }

  // Expand or collapse the detail named by the clicked button's aria-controls.
  // Returns the new expanded state so toggleEditMode can follow it rather than
  // keeping a second flag that could drift out of step with the markup.
  toggleRow(event) {
    const button = event.currentTarget
    const detail = document.getElementById(button.getAttribute("aria-controls"))
    if (!detail) return false

    const expanded = detail.hidden
    detail.hidden = !expanded
    button.setAttribute("aria-expanded", String(expanded))
    return expanded
  }

  switchLang(event) {
    if (this.isEditMode) return

    this.currentLang = event.currentTarget.dataset.lang
    const translation = this.translationsValue[this.currentLang]
    if (!translation) return

    this.previewSubjectTarget.textContent = translation.subject
    this.previewMessageTarget.textContent = translation.message

    this.subjectHiddenTarget.value = translation.subject
    this.messageHiddenTarget.value = translation.message
    if (this.hasLocaleHiddenTarget) this.localeHiddenTarget.value = this.currentLang

    for (const btn of this.langBtnTargets) {
      btn.dataset.active = btn.dataset.lang === this.currentLang ? "true" : "false"
    }

    // Each language has its own template chain, so the source line is a
    // property of the language, not of the document — leaving it behind reads
    // as "custom text" on a language that has none.
    this.#renderSource()
  }

  toggleEditMode() {
    if (this.isEditMode) this.#leaveEditMode()
    else this.#enterEditMode()
  }

  // The preview is the collapsed summary of the Správa row and stays on screen
  // while the editor is open, so it has to follow every keystroke — not only
  // the hidden field that is actually submitted.
  syncSubject() {
    this.subjectHiddenTarget.value = this.subjectFieldTarget.value
    this.previewSubjectTarget.textContent = this.subjectFieldTarget.value
  }

  syncMessage() {
    this.messageHiddenTarget.value = this.messageFieldTarget.value
    this.previewMessageTarget.textContent = this.messageFieldTarget.value
  }

  // CC and BCC in one line. "Bez kópie" needs BOTH fields empty: reading only
  // the field that just changed claimed no copies while the other still held
  // the workspace's standing accountant address.
  syncCopies() {
    const summary = this.copiesSummaryTarget
    const cc = this.#addresses(this.ccFieldTarget)
    const bcc = this.#addresses(this.bccFieldTarget)

    summary.replaceChildren()

    if (cc.length === 0 && bcc.length === 0) {
      summary.textContent = summary.dataset.noCopies
      return
    }

    if (cc.length > 0) summary.append(cc.join(", "))
    if (bcc.length === 0) return

    if (cc.length > 0) summary.append("; ")
    summary.append(`${bcc.join(", ")} `)

    // Built as a node rather than assigned as markup: these strings are
    // addresses the user just typed.
    const marker = document.createElement("span")
    marker.className = "text-muted"
    marker.textContent = `· ${summary.dataset.hiddenLabel}`
    summary.append(marker)
  }

  syncAttachments() {
    const summary = this.attachmentsSummaryTarget
    const checked = this.attachmentBoxTargets.filter((box) => box.checked).length
    // Pre-rendered per count by the server: Slovak alone needs four plural
    // forms, and no single string with a placeholder can produce them.
    const templates = JSON.parse(summary.dataset.summaryTemplates || "{}")
    const line = templates[checked]
    if (line) summary.textContent = line
  }

  // Comma OR semicolon, matching Documents::EmailSender#normalize_recipient_list
  // — a summary that disagreed with the sender about what "two recipients"
  // means would miscount exactly the Outlook-pasted list it exists to show.
  #addresses(field) {
    return field.value
      .split(/[;,]/)
      .map((address) => address.trim())
      .filter((address) => address !== "")
  }

  // A refused send re-renders this modal with the text the user TYPED, not the
  // template — that is the whole point of the error re-render. Left collapsed,
  // that text sits behind a closed editor and an unlocked picker whose next
  // click overwrites it from translationsValue, silently discarding the very
  // edit the send was refused over. So if the hidden fields no longer match
  // this language's template, reopen the editor the way the user left it.
  //
  // On a trial there is no toggle (editing is a paid feature) and the server
  // forces the default template, so there is nothing to restore.
  #reopenEditorForCustomText() {
    if (!this.hasEditToggleTarget) return

    const translation = this.translationsValue[this.currentLang]
    if (!translation) return
    if (
      this.subjectHiddenTarget.value === translation.subject &&
      this.messageHiddenTarget.value === translation.message
    ) {
      return
    }

    this.#enterEditMode()
  }

  #enterEditMode() {
    this.isEditMode = true
    this.#setEditorOpen(true)
    this.langPickerTarget.classList.add("send-email-lang-picker--disabled")
    this.editToggleTarget.textContent = this.editToggleTarget.dataset.resetLabel
    this.editToggleTarget.classList.add("send-email-row__toggle--active")
    this.subjectFieldTarget.value = this.subjectHiddenTarget.value
    this.messageFieldTarget.value = this.messageHiddenTarget.value
  }

  // Closing the editor IS the reset — the button says so while it is open
  // ("Predvolená šablóna"), and the language picker it unlocks would otherwise
  // switch languages under a body the user had rewritten.
  #leaveEditMode() {
    this.isEditMode = false
    this.#setEditorOpen(false)
    this.langPickerTarget.classList.remove("send-email-lang-picker--disabled")
    this.editToggleTarget.textContent = this.editToggleTarget.dataset.editLabel
    this.editToggleTarget.classList.remove("send-email-row__toggle--active")

    const translation = this.translationsValue[this.currentLang]
    if (!translation) return

    this.previewSubjectTarget.textContent = translation.subject
    this.previewMessageTarget.textContent = translation.message
    this.subjectHiddenTarget.value = translation.subject
    this.messageHiddenTarget.value = translation.message
  }

  // The same two attributes #toggleRow flips, so the Správa row cannot end up
  // half-open — connect() opens the editor without any click to read.
  #setEditorOpen(open) {
    this.editModeTarget.hidden = !open
    this.editToggleTarget.setAttribute("aria-expanded", String(open))
  }

  #renderSource() {
    if (!this.hasSourceTarget) return

    const element = this.sourceTarget
    const sources = JSON.parse(element.dataset.sources || "{}")
    const labels = JSON.parse(element.dataset.labels || "{}")
    const label = labels[sources[this.currentLang] || "default"]
    if (label) element.textContent = label
  }
}
